import { createFileRoute } from '@tanstack/react-router';
import type { IUser } from 'common-types';
import { DEFAULT_PROFILE, QueryKey } from 'common-types';
import { insightsQueryKeys } from 'hooks/insights/query-keys';
import { InsightsCategorySkeleton } from 'pages/insights-category/components/skeleton';
import { RECOMMENDED_CATEGORY_ID } from 'pages/insights-category/recommendations-section';
import { queryClient } from 'providers/react-query';
import { lazy, Suspense } from 'react';
import { insightsCategorySearchSchema } from 'router/schemas';
import { getArticles, getCollections } from 'services/strapi/strapi';

const InsightsCategory = lazy(() => import('pages/insights-category'));

export const Route = createFileRoute('/_authenticated/_main/insights/$categoryId')({
  validateSearch: insightsCategorySearchSchema,
  loaderDeps: ({ search: { type } }) => ({ type }),
  loader: ({ params, deps: { type } }) => {
    const userData = queryClient.getQueryData<IUser>([QueryKey.USER_DATA]);
    const profile = userData?.profileName ?? DEFAULT_PROFILE;
    const isRecommended = params.categoryId === RECOMMENDED_CATEGORY_ID;
    const filters = isRecommended ? {} : { categories: { id: { $eq: params.categoryId } } };

    if (type === 'articles' || type === 'mixed') {
      void queryClient.prefetchInfiniteQuery({
        queryKey: insightsQueryKeys.articles({ filters, sort: 'createdAt:desc', pageSize: 25, profile }),
        queryFn: ({ signal }) =>
          getArticles({ page: 1, pageSize: 25, filters, sort: 'createdAt:desc', signal, profile }),
        initialPageParam: 1,
      });
    }

    if (type === 'collections' || type === 'mixed') {
      void queryClient.prefetchInfiniteQuery({
        queryKey: insightsQueryKeys.collections({ filters, sort: 'createdAt:desc', pageSize: 25, profile }),
        queryFn: ({ signal }) =>
          getCollections({ page: 1, pageSize: 25, filters, sort: 'createdAt:desc', signal, profile }),
        initialPageParam: 1,
      });
    }
  },
  component: InsightsCategoryWrapper,
});

function InsightsCategoryWrapper() {
  return (
    <Suspense fallback={<InsightsCategorySkeleton />}>
      <InsightsCategory />
    </Suspense>
  );
}
